import moment from 'moment';

import type { MatrixSelectionMeta } from './getMatrixSelectionMeta';

export type MatrixExceptionDateRange = {
  startDate: string;
  endDate: string;
};

/**
 * Collapses sorted YYYY-MM-DD dates into contiguous ranges without filling gaps.
 * e.g. [Mon, Tue, Thu] → [{Mon–Tue}, {Thu–Thu}].
 */
export const collapseSortedDatesToRanges = (dates: string[]): MatrixExceptionDateRange[] => {
  if (dates.length === 0) return [];

  const sorted = Array.from(new Set(dates)).sort();
  const ranges: MatrixExceptionDateRange[] = [];
  let startDate = sorted[0];
  let endDate = sorted[0];

  for (let i = 1; i < sorted.length; i += 1) {
    const nextDate = sorted[i];
    const expectedNext = moment(endDate, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');
    if (nextDate === expectedNext) {
      endDate = nextDate;
      continue;
    }
    ranges.push({ startDate, endDate });
    startDate = nextDate;
    endDate = nextDate;
  }

  ranges.push({ startDate, endDate });
  return ranges;
};

/**
 * When the drawer keeps the selection min–max bounds, persist only selected days
 * (gapped ⌘ selection → discrete ranges). If the user edits the form range, use that
 * contiguous start/end instead.
 */
export const resolveMatrixExceptionDateRanges = ({
  formStartDate,
  formEndDate,
  selectionMeta,
}: {
  formStartDate: string;
  formEndDate: string;
  selectionMeta: MatrixSelectionMeta;
}): MatrixExceptionDateRange[] => {
  if (!formStartDate) return [];

  const endDate = formEndDate || formStartDate;
  if (
    formStartDate === selectionMeta.startDate &&
    endDate === selectionMeta.endDate
  ) {
    return collapseSortedDatesToRanges(selectionMeta.dates);
  }

  return [{ startDate: formStartDate, endDate }];
};
